import Long from 'long';
import { PPCBound3D } from './PPCBound3D';
import { MortonCode, PPCLevel, PPCOctant, PPCVoxelId } from './PPCI';
import { PPCPoint2D } from './PPCPoint2D';
import { PPCPoint3D } from './PPCPoint3D';

const kRootLevel = 0;
const kMaxLevel = 21;
const kRootVoxelId = new Long(0, 0, true);

let mFirstOfLevels: Array<PPCVoxelId>;
let mVoxelSizes: Uint32Array;
let mVoxelHalfSizes: Uint32Array;

// method to seperate bits from a given integer 3 positions apart
function SeperateBits(val: number): Long {
  const seperateShift32 = new Long(0x0000ffff, 0x1f0000, true);
  const seperateShift16 = new Long(0xff0000ff, 0x1f0000, true);
  const seperateShift8 = new Long(0x0f00f00f, 0x100f00f0, true);
  const seperateShift4 = new Long(0xc30c30c3, 0x10c30c30, true);
  const seperateShift2 = new Long(0x49249249, 0x12492492, true);

  let x = new Long(val & 0x1fffff, 0, true); // we only look at the first 21 bits

  x = x.or(x.shiftLeft(32)).and(seperateShift32); // shift left 32 bits, OR with self, and 00011111000000000000000000000000000000001111111111111111
  x = x.or(x.shiftLeft(16)).and(seperateShift16); // shift left 32 bits, OR with self, and 00011111000000000000000011111111000000000000000011111111
  x = x.or(x.shiftLeft(8)).and(seperateShift8); // shift left 32 bits, OR with self, and 0001000000001111000000001111000000001111000000001111000000000000
  x = x.or(x.shiftLeft(4)).and(seperateShift4); // shift left 32 bits, OR with self, and 0001000011000011000011000011000011000011000011000011000100000000
  x = x.or(x.shiftLeft(2)).and(seperateShift2);
  return x;
}

export function EncodeMortion(inCordinate: PPCPoint2D | PPCPoint3D): MortonCode {
  let answer = new Long(0, 0, true);
  answer = SeperateBits(inCordinate.x).or(SeperateBits(inCordinate.y).shiftLeft(1));
  if (inCordinate instanceof PPCPoint3D) {
    answer = answer.or(SeperateBits(inCordinate.z).shiftLeft(2));
  }
  return answer;
}

function GroupBits(inCode: MortonCode): Long {
  const gpAnd = new Long(0x49249249, 0x12492492, true);
  const gpShRight2 = new Long(0xc30c30c3, 0x10c30c30, true);
  const gpShRight4 = new Long(0x0f00f00f, 0x100f00f0, true);
  const gpShRight8 = new Long(0xff0000ff, 0x1f0000, true);
  const gpShRight16 = new Long(0x0000ffff, 0x1f0000, true);

  let val = inCode.and(gpAnd); //                     (* 0001 0010 0100 1001 0010 0100 1001 0010 0100 1001 0010 0100 1001 0010 0100 1001 *)  (* val = ---- 9--8 --7- -6-- 5--4 --3- -2-- 1--0 *)
  val = val.or(val.shiftRight(2)).and(gpShRight2); //       (* 0001000011000011000011000011000011000011000011000011000100000000 *)  (* val = ---- --98 ---- 76-- --54 ---- 32-- --10 *)
  val = val.or(val.shiftRight(4)).and(gpShRight4); //        (* 0001000000001111000000001111000000001111000000001111000000000000 *)  (* val = ---- --98 ---- ---- 7654 ---- ---- 3210 *)
  val = val.or(val.shiftRight(8)).and(gpShRight8); //          (* 0000000000011111000000000000000011111111000000000000000011111111 *)  (* val = ---- --98 ---- ---- ---- ---- 7654 3210 *)
  val = val.or(val.shiftRight(16)).and(gpShRight16); //         (* 0000000000011111000000000000000000000000000000001111111111111111 *)  (* val = ---- ---- ---- ---- ---- --98 7654 3210 *)
  val = val.or(val.shiftRight(32)).and(0x1fffff); //         (* 000111111111111111111111 *)                                          (* val = ---- ---- ---- ---- ---- --98 7654 3210 *)
  return val;
}

export function DecodeMorton2D(inMortonCode: MortonCode): PPCPoint2D {
  const result = new PPCPoint2D();
  result.x = GroupBits(inMortonCode).toInt();
  result.y = GroupBits(inMortonCode.shiftRight(1)).toInt();
  return result;
}

export function DecodeMorton3D(inMortonCode: MortonCode): PPCPoint3D {
  const result = new PPCPoint3D();
  result.x = GroupBits(inMortonCode).toInt();
  result.y = GroupBits(inMortonCode.shiftRight(1)).toInt();
  result.z = GroupBits(inMortonCode.shiftRight(2)).toInt();
  return result;
}

export function CalcVoxelCenter2D(inVoxelId: PPCVoxelId, inDepth: PPCLevel): PPCPoint2D {
  let voxelHalfSize = 0; //CARDINAL32 = 0;
  let coord: PPCPoint2D = new PPCPoint2D();

  let morton = inVoxelId.subtract(mFirstOfLevels[inDepth]);
  voxelHalfSize = mVoxelHalfSizes[inDepth];
  morton = morton.shiftLeft(3 * (kMaxLevel - inDepth));
  coord = DecodeMorton2D(morton);
  coord.x += voxelHalfSize;
  coord.y += voxelHalfSize;
  return coord;
}

export function CalcVoxelCenter3D(inVoxelId: PPCVoxelId, inDepth: PPCLevel): PPCPoint3D {
  let voxelHalfSize = 0; //CARDINAL32 = 0;
  let coord: PPCPoint3D = new PPCPoint3D();

  let morton = inVoxelId.subtract(mFirstOfLevels[inDepth]);
  if (inDepth < kMaxLevel) {
    voxelHalfSize = mVoxelHalfSizes[inDepth];
  }
  morton = morton.shiftLeft(3 * (kMaxLevel - inDepth));
  coord = DecodeMorton3D(morton);
  coord.x += voxelHalfSize;
  coord.y += voxelHalfSize;
  coord.z += voxelHalfSize;

  return coord;
}

export function CalcVoxelId(inCoordinate: PPCPoint3D | PPCPoint2D, inDepth: PPCLevel): PPCVoxelId {
  const mortonLeaf = EncodeMortion(inCoordinate);
  const morton = mortonLeaf.shiftRight(3 * (kMaxLevel - inDepth));
  return morton.add(mFirstOfLevels[inDepth]);
}

export function GetVoxelBox(inVoxelId: PPCVoxelId, inDepth: PPCLevel): PPCBound3D {
  let voxelHalfSize = 0; //CARDINAL32 = 0;

  const center = CalcVoxelCenter3D(inVoxelId, inDepth);
  const outPt1 = new PPCPoint3D();
  const outPt2 = new PPCPoint3D();
  if (inDepth < kMaxLevel) {
    voxelHalfSize = mVoxelHalfSizes[inDepth] & 0xffffffff;
    outPt1.x = center.x - voxelHalfSize;
    outPt1.y = center.y - voxelHalfSize;
    outPt1.z = center.z - voxelHalfSize;
    outPt2.x = center.x + voxelHalfSize;
    outPt2.y = center.y + voxelHalfSize;
    outPt2.z = center.z + voxelHalfSize;
  } else {
    outPt1.x = center.x;
    outPt1.y = center.y;
    outPt1.z = center.z;
  }
  return new PPCBound3D(outPt1, outPt2);
}

export function GetChildId(inParentId: PPCVoxelId, inOctant: PPCOctant): PPCVoxelId {
  return inParentId.shiftLeft(3).add(inOctant + 1);
}

export function GetOctant(inVoxelId: PPCVoxelId): PPCOctant {
  const seven = new Long(0x00000007, 0x00000000, true);

  if (inVoxelId === kRootVoxelId) {
    return PPCOctant.DownNorthEast;
  }
  return inVoxelId.add(-1).and(seven).toInt();
}

export function GetParentId(inVoxelId: PPCVoxelId): PPCVoxelId {
  if (inVoxelId === kRootVoxelId) {
    return kRootVoxelId;
  }
  return inVoxelId.add(-1).shiftRight(3);
}

export function GetRangeOfSubtree(inRootId: PPCVoxelId, inSubtreeLevel: PPCLevel): [PPCVoxelId, PPCVoxelId] {
  let level: PPCLevel;

  let outFirstId = inRootId;
  let outLastId = inRootId;
  level = GetLevel(inRootId);

  while (level < inSubtreeLevel) {
    outFirstId = GetChildId(outFirstId, PPCOctant.DownSouthWest);
    outLastId = GetChildId(outLastId, PPCOctant.UpNorthEast);
    level++;
  }
  return [outFirstId, outLastId];
}

export function GetLevel(inVoxelId: PPCVoxelId): PPCLevel {
  let from: PPCLevel = kRootLevel;
  let to: PPCLevel = kMaxLevel;

  while (from + 1 < to) {
    const middle = Math.floor((from + to) / 2.0);
    if (inVoxelId.greaterThanOrEqual(mFirstOfLevels[middle])) {
      from = middle;
    } else {
      to = middle;
    }
  }
  if (from === to || inVoxelId.greaterThanOrEqual(mFirstOfLevels[to])) {
    return to;
  }
  if (inVoxelId.greaterThanOrEqual(mFirstOfLevels[from + 1])) {
    return from;
  }
  return from;
}
export function GetNumberAtLevel(inLevel: PPCLevel): PPCVoxelId {
  return new Long(1, 0, true).shiftLeft(inLevel * 3);
}

export function InitFirstOfLevels(): void {
  mFirstOfLevels = new Array<PPCVoxelId>();
  let level: PPCLevel;
  for (level = kRootLevel; level <= kMaxLevel + 1; level++) {
    if (level === kRootLevel) {
      mFirstOfLevels[level] = kRootVoxelId;
    } else {
      mFirstOfLevels[level] = GetNumberAtLevel(level - 1).add(mFirstOfLevels[level - 1]);
    }
  }
}

export function InitVoxelSizes() {
  let level: PPCLevel;
  mVoxelSizes = new Uint32Array(kMaxLevel + 2);
  mVoxelHalfSizes = new Uint32Array(kMaxLevel + 2);
  for (level = kRootLevel; level < kMaxLevel; level++) {
    mVoxelSizes[level] = 0x00000001 << (kMaxLevel - level);
    mVoxelHalfSizes[level] = 0x00000001 << (kMaxLevel - level - 1);
  }
  mVoxelSizes[kMaxLevel] = 1;
  mVoxelSizes[kMaxLevel + 1] = 1;
  mVoxelHalfSizes[kMaxLevel] = 0;
  mVoxelHalfSizes[kMaxLevel + 1] = 0;
}

InitFirstOfLevels();
InitVoxelSizes();
